import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Eye, Timer, CheckCircle, WarningCircle, Siren, UserCircle, Monitor, ListBullets, Layout, GearSix, SpeakerHigh, SpeakerSlash } from "@phosphor-icons/react";

const fmt = (min) => {
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 24 ? `${Math.floor(h / 24)}d ${h % 24}h` : `${h}h ${m}m`;
};

const alertColor = (level) => {
  if (level === "red" || level === "critical") return { bg: "bg-red-900/30", border: "border-red-500", text: "text-red-400", pulse: "animate-pulse", glow: "shadow-red-500/20 shadow-lg" };
  if (level === "yellow" || level === "warning_60" || level === "warning_30") return { bg: "bg-amber-900/20", border: "border-amber-500", text: "text-amber-400", pulse: "", glow: "" };
  if (level === "warning_15") return { bg: "bg-sky-900/20", border: "border-sky-500", text: "text-sky-400", pulse: "", glow: "" };
  return { bg: "bg-emerald-900/20", border: "border-emerald-500", text: "text-emerald-400", pulse: "", glow: "" };
};

const statusLabel = (s) => ({ nuevo: "Nuevo", abierto: "Abierto", en_progreso: "En Progreso", pendiente: "Pendiente", en_espera: "En Espera", resuelto: "Resuelto", cerrado: "Cerrado" }[s] || s);

export default function TicketMonitor() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [view, setView] = useState("kanban");
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [lastCount, setLastCount] = useState(0);
  const audioRef = useRef(null);
  const intervalRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tickets/monitor/live`, { headers: { Authorization: `Bearer ${token}` } });
      const newData = res.data;
      // Play alert sound if new urgent tickets
      if (soundEnabled && newData.stats.total_open > lastCount && lastCount > 0) {
        playAlert();
      }
      setLastCount(newData.stats.total_open);
      setData(newData);
    } catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
  }, [token, soundEnabled, lastCount]);

  useEffect(() => {
    load();
    intervalRef.current = setInterval(load, 15000);
    return () => clearInterval(intervalRef.current);
  }, [load]);

  const playAlert = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 880;
      osc.type = "sine";
      gain.gain.value = 0.3;
      osc.start();
      setTimeout(() => { osc.frequency.value = 660; }, 150);
      setTimeout(() => { osc.frequency.value = 880; }, 300);
      setTimeout(() => { osc.stop(); ctx.close(); }, 500);
    } catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
  };

  if (!data) return <div className="flex items-center justify-center h-96 text-slate-400">Cargando monitor...</div>;

  const { active_tickets, recent_resolved, stats } = data;

  const urgent = active_tickets.filter(t => t.alert_level === "red" || t.alert_level === "critical");
  const warning = active_tickets.filter(t => t.alert_level === "yellow" || t.alert_level === "warning_60" || t.alert_level === "warning_30");
  const normal = active_tickets.filter(t => !["red", "critical", "yellow", "warning_60", "warning_30"].includes(t.alert_level));

  return (
    <div className="space-y-4" data-testid="ticket-monitor">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Monitor size={24} /> Visualizador de Tickets</h1>
        <div className="flex items-center gap-2">
          <div className="flex bg-slate-100 rounded-lg p-0.5">
            <button onClick={() => setView("kanban")} className={`px-3 py-1.5 text-xs font-medium rounded-md ${view === "kanban" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`} data-testid="monitor-view-kanban"><Layout size={14} className="inline mr-1" />Kanban</button>
            <button onClick={() => setView("list")} className={`px-3 py-1.5 text-xs font-medium rounded-md ${view === "list" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`} data-testid="monitor-view-list"><ListBullets size={14} className="inline mr-1" />Lista</button>
            <button onClick={() => setView("tv")} className={`px-3 py-1.5 text-xs font-medium rounded-md ${view === "tv" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`} data-testid="monitor-view-tv"><Monitor size={14} className="inline mr-1" />Monitor</button>
          </div>
          <button onClick={() => setSoundEnabled(!soundEnabled)} className={`p-2 rounded-lg ${soundEnabled ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-400"}`} title={soundEnabled ? "Sonido activado" : "Sonido silenciado"} data-testid="monitor-sound-toggle">
            {soundEnabled ? <SpeakerHigh size={16} /> : <SpeakerSlash size={16} />}
          </button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="monitor-stats">
        <div className="bg-white rounded-xl border p-3"><p className="text-[10px] text-slate-500 uppercase font-bold">Abiertos</p><p className="text-2xl font-black text-slate-900">{stats.total_open}</p></div>
        <div className={`rounded-xl border p-3 ${stats.urgent > 0 ? "bg-red-50 border-red-200" : "bg-white"}`}><p className="text-[10px] text-red-600 uppercase font-bold">Urgentes</p><p className="text-2xl font-black text-red-700">{stats.urgent}</p></div>
        <div className="bg-white rounded-xl border p-3"><p className="text-[10px] text-amber-600 uppercase font-bold">En Progreso</p><p className="text-2xl font-black text-amber-700">{stats.in_progress}</p></div>
        <div className="bg-white rounded-xl border p-3"><p className="text-[10px] text-emerald-600 uppercase font-bold">Resueltos Hoy</p><p className="text-2xl font-black text-emerald-700">{stats.resolved_today}</p></div>
        <div className="bg-white rounded-xl border p-3"><p className="text-[10px] text-slate-500 uppercase font-bold">Tiempo Prom.</p><p className="text-2xl font-black text-blue-700">{fmt(stats.avg_minutes)}</p></div>
      </div>

      {/* Views */}
      {view === "kanban" && <KanbanView urgent={urgent} warning={warning} normal={normal} resolved={recent_resolved} />}
      {view === "list" && <ListView tickets={active_tickets} resolved={recent_resolved} />}
      {view === "tv" && <TVView urgent={urgent} warning={warning} normal={normal} stats={stats} resolved={recent_resolved} />}
    </div>
  );
}

function TicketCard({ ticket, size = "normal" }) {
  const c = alertColor(ticket.alert_level);
  const isLarge = size === "large";
  return (
    <div className={`${c.bg} border ${c.border} rounded-lg p-3 ${c.pulse} ${c.glow} transition-all`} data-testid={`monitor-card-${ticket.id}`}>
      <div className="flex items-start justify-between mb-1.5">
        <span className={`font-mono font-black ${isLarge ? "text-lg" : "text-sm"} ${c.text}`}>{ticket.ticket_number}</span>
        <span className={`font-mono font-bold ${isLarge ? "text-base" : "text-xs"} ${c.text} tabular-nums`}>{fmt(ticket.minutes_elapsed)}</span>
      </div>
      <p className={`${isLarge ? "text-sm" : "text-xs"} text-slate-700 font-medium truncate`}>{ticket.description || ticket.hardware_type || "Sin descripcion"}</p>
      <div className={`flex items-center justify-between mt-2 ${isLarge ? "text-xs" : "text-[10px]"} text-slate-500`}>
        <span className="flex items-center gap-1"><UserCircle size={isLarge ? 14 : 12} /> {ticket.engineer_name || "Sin asignar"}</span>
        <span className="bg-slate-200/70 px-1.5 py-0.5 rounded text-slate-600 font-semibold">{statusLabel(ticket.status)}</span>
      </div>
      {ticket.company && <p className={`${isLarge ? "text-xs" : "text-[10px]"} text-slate-400 mt-1 truncate`}>{ticket.company}</p>}
    </div>
  );
}

function KanbanView({ urgent, warning, normal, resolved }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="kanban-view">
      {/* Urgent Column */}
      <div>
        <div className="bg-red-600 text-white px-3 py-2 rounded-t-lg flex items-center gap-2 font-bold text-sm"><Siren size={16} weight="fill" /> URGENTE ({urgent.length})</div>
        <div className="bg-red-50/50 border border-t-0 border-red-200 rounded-b-lg p-2 space-y-2 min-h-[120px]">
          {urgent.length === 0 ? <p className="text-center text-xs text-red-300 py-6">Sin urgentes</p> : urgent.map(t => <TicketCard key={t.id} ticket={t} />)}
        </div>
      </div>
      {/* Warning Column */}
      <div>
        <div className="bg-amber-500 text-white px-3 py-2 rounded-t-lg flex items-center gap-2 font-bold text-sm"><WarningCircle size={16} weight="fill" /> EN PROGRESO ({warning.length})</div>
        <div className="bg-amber-50/50 border border-t-0 border-amber-200 rounded-b-lg p-2 space-y-2 min-h-[120px]">
          {warning.length === 0 ? <p className="text-center text-xs text-amber-300 py-6">Sin tickets</p> : warning.map(t => <TicketCard key={t.id} ticket={t} />)}
        </div>
      </div>
      {/* Normal Column */}
      <div>
        <div className="bg-emerald-500 text-white px-3 py-2 rounded-t-lg flex items-center gap-2 font-bold text-sm"><CheckCircle size={16} weight="fill" /> NORMAL ({normal.length})</div>
        <div className="bg-emerald-50/50 border border-t-0 border-emerald-200 rounded-b-lg p-2 space-y-2 min-h-[120px]">
          {normal.length === 0 ? <p className="text-center text-xs text-emerald-300 py-6">Sin tickets</p> : normal.map(t => <TicketCard key={t.id} ticket={t} />)}
        </div>
      </div>
      {/* Resolved Column */}
      <div>
        <div className="bg-slate-600 text-white px-3 py-2 rounded-t-lg flex items-center gap-2 font-bold text-sm"><CheckCircle size={16} /> RESUELTOS ({resolved.length})</div>
        <div className="bg-slate-50 border border-t-0 border-slate-200 rounded-b-lg p-2 space-y-2 min-h-[120px] max-h-[500px] overflow-y-auto">
          {resolved.slice(0, 10).map(t => <TicketCard key={t.id} ticket={{ ...t, alert_level: "green" }} />)}
        </div>
      </div>
    </div>
  );
}

function ListView({ tickets, resolved }) {
  const all = [...tickets, ...resolved.slice(0, 10).map(t => ({ ...t, alert_level: "green" }))];
  return (
    <div className="bg-white rounded-xl border overflow-x-auto" data-testid="list-view">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-slate-800 text-white">
            <th className="py-2.5 px-3 text-left font-bold">Ticket</th>
            <th className="py-2.5 px-3 text-left font-bold">Tiempo</th>
            <th className="py-2.5 px-3 text-left font-bold">Descripcion</th>
            <th className="py-2.5 px-3 text-left font-bold">Equipo</th>
            <th className="py-2.5 px-3 text-left font-bold">Asignado</th>
            <th className="py-2.5 px-3 text-center font-bold">Estatus</th>
            <th className="py-2.5 px-3 text-center font-bold">Prioridad</th>
          </tr>
        </thead>
        <tbody>
          {all.map(t => {
            const c = alertColor(t.alert_level);
            return (
              <tr key={t.id} className={`border-t ${c.bg} hover:opacity-90 transition-opacity ${t.alert_level === "red" ? "animate-pulse" : ""}`} data-testid={`list-row-${t.id}`}>
                <td className={`py-2 px-3 font-mono font-black ${c.text}`}>{t.ticket_number}</td>
                <td className={`py-2 px-3 font-mono font-bold ${c.text} tabular-nums`}>{fmt(t.minutes_elapsed || 0)}</td>
                <td className="py-2 px-3 text-slate-700 max-w-[200px] truncate">{t.description || t.hardware_type || "\u2014"}</td>
                <td className="py-2 px-3 text-slate-600">{t.hardware_type || "\u2014"}</td>
                <td className="py-2 px-3 text-slate-700 font-medium">{t.engineer_name || <span className="text-slate-400">Sin asignar</span>}</td>
                <td className="py-2 px-3 text-center"><span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${c.text} ${c.bg} border ${c.border}`}>{statusLabel(t.status)}</span></td>
                <td className="py-2 px-3 text-center text-slate-600">{t.priority || "\u2014"}</td>
              </tr>
            );
          })}
          {all.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-slate-400">Sin tickets activos</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function TVView({ urgent, warning, normal, stats, resolved }) {
  return (
    <div className="bg-slate-900 rounded-2xl p-6 space-y-5 min-h-[500px]" data-testid="tv-view">
      {/* TV Stats */}
      <div className="grid grid-cols-5 gap-3">
        <div className="bg-slate-800 rounded-xl p-4 text-center"><p className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Abiertos</p><p className="text-4xl font-black text-white tabular-nums">{stats.total_open}</p></div>
        <div className={`rounded-xl p-4 text-center ${stats.urgent > 0 ? "bg-red-900/50 ring-1 ring-red-500" : "bg-slate-800"}`}><p className="text-[10px] text-red-400 uppercase font-bold tracking-wider">Urgentes</p><p className={`text-4xl font-black tabular-nums ${stats.urgent > 0 ? "text-red-400 animate-pulse" : "text-slate-500"}`}>{stats.urgent}</p></div>
        <div className="bg-slate-800 rounded-xl p-4 text-center"><p className="text-[10px] text-amber-400 uppercase font-bold tracking-wider">En Progreso</p><p className="text-4xl font-black text-amber-400 tabular-nums">{stats.in_progress}</p></div>
        <div className="bg-slate-800 rounded-xl p-4 text-center"><p className="text-[10px] text-emerald-400 uppercase font-bold tracking-wider">Resueltos Hoy</p><p className="text-4xl font-black text-emerald-400 tabular-nums">{stats.resolved_today}</p></div>
        <div className="bg-slate-800 rounded-xl p-4 text-center"><p className="text-[10px] text-blue-400 uppercase font-bold tracking-wider">T. Promedio</p><p className="text-4xl font-black text-blue-400 tabular-nums">{fmt(stats.avg_minutes)}</p></div>
      </div>

      {/* TV Tickets Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Urgent */}
        <div>
          <h3 className="text-red-400 text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5"><Siren size={14} weight="fill" /> Urgentes ({urgent.length})</h3>
          <div className="space-y-2">
            {urgent.length === 0 ? <div className="bg-slate-800 rounded-lg p-4 text-center text-slate-600 text-xs">Sin urgentes</div> : urgent.map(t => <TicketCard key={t.id} ticket={t} size="large" />)}
          </div>
        </div>
        {/* In Progress */}
        <div>
          <h3 className="text-amber-400 text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5"><Timer size={14} weight="fill" /> En Progreso ({warning.length})</h3>
          <div className="space-y-2">
            {warning.length === 0 ? <div className="bg-slate-800 rounded-lg p-4 text-center text-slate-600 text-xs">Sin tickets</div> : warning.map(t => <TicketCard key={t.id} ticket={t} size="large" />)}
          </div>
        </div>
        {/* Normal + Resolved */}
        <div>
          <h3 className="text-emerald-400 text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5"><CheckCircle size={14} weight="fill" /> Normal / Resueltos ({normal.length + resolved.length})</h3>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {normal.map(t => <TicketCard key={t.id} ticket={t} size="large" />)}
            {resolved.slice(0, 5).map(t => <TicketCard key={t.id} ticket={{ ...t, alert_level: "green" }} size="large" />)}
            {normal.length === 0 && resolved.length === 0 && <div className="bg-slate-800 rounded-lg p-4 text-center text-slate-600 text-xs">Sin tickets</div>}
          </div>
        </div>
      </div>

      {/* Scrolling Message */}
      <div className="bg-slate-800 rounded-lg p-3 overflow-hidden">
        <div className="animate-marquee whitespace-nowrap text-xs text-amber-400 font-medium">
          {stats.urgent > 0 ? `ATENCION: ${stats.urgent} ticket(s) urgente(s) requieren atencion inmediata. ` : ""}
          Tickets abiertos: {stats.total_open} | Resueltos hoy: {stats.resolved_today} | Tiempo promedio: {fmt(stats.avg_minutes)}
          {stats.urgent > 0 ? ` | ATENCION: ${stats.urgent} ticket(s) urgente(s) requieren atencion inmediata.` : ""}
        </div>
      </div>
    </div>
  );
}
